import React, { useEffect, useMemo, useState } from 'react';
import { api, API } from '../lib/api';
import { Loader2, Download, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_LABEL = {
  confirm_payment: { label: 'Payment confirmed', tone: '#1F5A24', bg: '#E8F5E9' },
  mark_incomplete: { label: 'Marked incomplete', tone: '#B26A00', bg: '#FFF3E0' },
  check_in: { label: 'Checked in', tone: '#1F5A24', bg: '#C8E6C9' },
  check_in_override: { label: 'Check-in override', tone: '#8f5500', bg: '#FFF3E0' },
};

function ActionBadge({ action }) {
  const cfg = ACTION_LABEL[action] || { label: action, tone: '#333', bg: '#EEE' };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.tone }}
    >
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: cfg.tone }} />
      {cfg.label}
    </span>
  );
}

function prettyValue(v) {
  if (v == null || v === '') return '—';
  if (v.length > 10 && v.match(/^\d{4}-\d{2}-\d{2}T/)) {
    try { return new Date(v).toLocaleString('en-GB'); } catch {}
  }
  return v;
}

export default function AuditLog() {
  const [entries, setEntries] = useState(null);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_email: '', booking: '', date_from: '', date_to: '' });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const { data } = await api.get(`/audit?${params.toString()}`);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Could not load the audit log');
      setEntries([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    api.get('/staff').then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const applyFilters = () => load();
  const clearFilters = () => { setFilters({ user_email: '', booking: '', date_from: '', date_to: '' }); setTimeout(load, 0); };

  const download = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const res = await fetch(`${API}/audit/export.csv?${params.toString()}`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('bb_token') || ''}`,
        },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export the audit log');
    }
  };

  const grouped = useMemo(() => {
    if (!entries) return [];
    const buckets = new Map();
    for (const e of entries) {
      const day = (e.at || '').slice(0, 10);
      if (!buckets.has(day)) buckets.set(day, []);
      buckets.get(day).push(e);
    }
    return Array.from(buckets.entries());
  }, [entries]);

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Audit log</h1>
          <p className="text-[#333]/70 mt-1">Every confirmation, rejection, check-in, and override, in one place.</p>
        </div>
        <button
          data-testid="audit-export"
          onClick={download}
          className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-4 mb-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">Staff member</label>
            <select
              data-testid="audit-user"
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-3 py-2 min-h-[2.75rem]"
            >
              <option value="">All staff</option>
              {users.map((u) => (
                <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">Booking number</label>
            <input
              data-testid="audit-booking"
              value={filters.booking}
              onChange={(e) => setFilters({ ...filters, booking: e.target.value })}
              placeholder="BK-…"
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-3 py-2 min-h-[2.75rem] font-mono"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">From</label>
            <input
              data-testid="audit-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-3 py-2 min-h-[2.75rem]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">To</label>
            <input
              data-testid="audit-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-3 py-2 min-h-[2.75rem]"
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            data-testid="audit-apply"
            onClick={applyFilters}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium"
          >
            <Filter size={14}/> Apply
          </button>
          <button
            data-testid="audit-clear"
            onClick={clearFilters}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5]"
          >
            Clear
          </button>
          <button
            onClick={load}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5]"
            aria-label="Refresh audit list"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/>
          </button>
        </div>
      </div>

      {/* Results */}
      {entries === null ? (
        <div className="flex items-center gap-2 text-[#333]/70"><Loader2 className="animate-spin" size={16}/> Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-[#333]/60 bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl p-8 text-center">
          No activity matches these filters yet. Try widening the date range.
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-sm text-[#333]/60">{total} event{total === 1 ? '' : 's'} · showing {entries.length}</div>
          {grouped.map(([day, rows]) => (
            <section key={day}>
              <h2 className="font-heading text-lg text-[#1F5A24] mb-2">
                {day ? new Date(day).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Unknown day'}
              </h2>
              <ul className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl overflow-hidden divide-y divide-[#CFD8CF]">
                {rows.map((e, i) => (
                  <li key={i} data-testid={`audit-row-${i}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-28 shrink-0">
                      <ActionBadge action={e.action}/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium">{e.user_name || e.user_email}</span>
                        <span className="text-xs text-[#333]/60 font-mono">{e.booking_number}</span>
                      </div>
                      <div className="text-sm text-[#333]/80 mt-0.5">
                        {e.old_value ? <span>from <em>{prettyValue(e.old_value)}</em> to <em>{prettyValue(e.new_value)}</em></span>
                          : <span>set to <em>{prettyValue(e.new_value)}</em></span>}
                      </div>
                      {e.reason && (
                        <div className="mt-1 inline-block text-xs text-[#8f5500] bg-[#FFF3E0] rounded px-2 py-0.5">
                          Reason: {e.reason}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-[#333]/60 shrink-0 sm:w-32 sm:text-right">
                      {e.at ? new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
